import MassImportUpload from '@/components/Upload/MassImportUpload';
import { FileUploadData, PreviewFile, useFileUpload } from '@/hooks/upload/FileUpload';
import React, { forwardRef, Ref, useImperativeHandle } from 'react';
import _ from 'underscore';
import LessonThumbUpload from './LessonThumbUpload';
import PreviewUpload from './PreviewUpload';
import SelectFileUpload from './SelectFileUpload';
import './style.scss';

export type TypeAccepted = 'IMAGE' | 'VIDEO' | 'AUDIO' | 'SUBTITLE' | 'ZIP' | 'ATTACHMENTS' | 'SPREADSHEET' | 'DOCUMENT';

export enum UploadType {
    DEFAULT = 'DEFAULT',
    LESSON_THUMB = 'LESSON_THUMB',
    MASS_IMPORT = 'MASS_IMPORT',
}

interface Props {
    typeAccepted: TypeAccepted;
    isLogo?: boolean;
    isBanner?: boolean;
    isFullImageTypeAccepted?: boolean;
    uploadType?: UploadType;
    disabled?: boolean;
    previewFile?: PreviewFile;
    removeFileLinkText?: string;
    fileOnQueueToConvert?: boolean;
    fileNameLimitSize?: number;
    onStart?: () => void;
    onComplete?: (response: FileUploadData) => void;
    onRemove?: () => void;
    onCancel?: () => void;
    onRemoveFileLinkClick?: () => void;
    onLoadedMetadata?: (minutes: number) => void;
    customFolder?: string;
}

const Upload = (
    {
        isLogo = false,
        isBanner = false,
        typeAccepted,
        uploadType = UploadType.DEFAULT,
        disabled = false,
        previewFile,
        removeFileLinkText,
        fileOnQueueToConvert,
        fileNameLimitSize,
        onStart,
        onComplete,
        onRemove,
        onCancel,
        onRemoveFileLinkClick,
        onLoadedMetadata,
        customFolder,
        isFullImageTypeAccepted = false,
    }: Props,
    ref: Ref<unknown> | undefined,
) => {
    const { fileUploadData, setFileUploadData, removeHandler } = useFileUpload({
        isLogo,
        isBanner,
        typeAccepted,
        previewFile,
        customFolder,
        isFullImageTypeAccepted,
    });
    const { preview } = fileUploadData;
    const showPreview = _.contains(['AUDIO', 'IMAGE', 'SUBTITLE', 'VIDEO', 'ZIP', 'DOCUMENT'], typeAccepted) && preview;

    useImperativeHandle(ref, () => ({
        clearFileInput: removeHandler,
        local: fileUploadData?.local,
    }));

    return (
        <div className="upload-container">
            {uploadType === UploadType.DEFAULT && (
                <>
                    {showPreview && (
                        <PreviewUpload
                            disabled={disabled}
                            fileOnQueueToConvert={fileOnQueueToConvert}
                            fileUploadData={fileUploadData}
                            isBanner={isBanner}
                            isLogo={isLogo}
                            onLoadedMetadata={onLoadedMetadata}
                            removeFileLinkText={removeFileLinkText}
                            removeHandler={() => removeHandler(onRemoveFileLinkClick, onRemove)}
                            typeAccepted={typeAccepted}
                        />
                    )}

                    {!showPreview && (
                        <SelectFileUpload
                            disabled={disabled}
                            fileNameLimitSize={fileNameLimitSize}
                            isBanner={isBanner}
                            isFullImageTypeAccepted={isFullImageTypeAccepted}
                            isLogo={isLogo}
                            onCancel={onCancel}
                            onComplete={(response) => {
                                setFileUploadData(response);
                                onComplete && onComplete(response);
                            }}
                            onRemove={onRemove}
                            onStart={onStart}
                            typeAccepted={typeAccepted}
                        />
                    )}
                </>
            )}

            {uploadType === UploadType.LESSON_THUMB && (
                <LessonThumbUpload disabled={disabled} onCancel={onCancel} onComplete={onComplete} onRemove={onRemove} typeAccepted={typeAccepted} />
            )}

            {uploadType === UploadType.MASS_IMPORT && (
                <MassImportUpload customFolder={customFolder} disabled={disabled} onComplete={onComplete} typeAccepted={typeAccepted} />
            )}
        </div>
    );
};

export default forwardRef(Upload);
